import React, { useState } from 'react'
import { useTheme } from '../contexts/ThemeContext'
import { Palette, RotateCcw, X } from 'lucide-react'

const ColorPicker = ({ isOpen, onClose }) => {
  const { customColors, updateCustomColor, resetCustomColors, theme, setTheme } = useTheme()
  const [activeColor, setActiveColor] = useState('primary')
  const [activeCategory, setActiveCategory] = useState('Principal')

  const colorOptions = [
    // Cores principais
    { key: 'primary', label: 'Cor Principal', description: 'Botões principais e links', category: 'Principal' },
    { key: 'secondary', label: 'Cor Secundária', description: 'Botões secundários', category: 'Principal' },
    { key: 'accent', label: 'Cor de Destaque', description: 'Elementos de destaque', category: 'Principal' },
    
    // Fundos
    { key: 'background', label: 'Fundo Principal', description: 'Cor de fundo da página', category: 'Fundos' },
    { key: 'card', label: 'Fundo dos Cartões', description: 'Fundo de cards e modais', category: 'Fundos' },
    { key: 'sidebar', label: 'Fundo da Sidebar', description: 'Fundo da barra lateral', category: 'Fundos' },
    { key: 'header', label: 'Fundo do Cabeçalho', description: 'Fundo da barra superior', category: 'Fundos' },
    
    // Textos
    { key: 'foreground', label: 'Texto Principal', description: 'Cor do texto principal', category: 'Textos' },
    { key: 'muted', label: 'Texto Secundário', description: 'Texto menos importante', category: 'Textos' },
    { key: 'textOnPrimary', label: 'Texto em Primário', description: 'Texto sobre cor principal', category: 'Textos' },
    { key: 'textOnSecondary', label: 'Texto em Secundário', description: 'Texto sobre cor secundária', category: 'Textos' },
    
    // Bordas e divisores
    { key: 'border', label: 'Bordas', description: 'Cor das bordas padrão', category: 'Bordas' },
    { key: 'borderHover', label: 'Bordas Hover', description: 'Bordas ao passar o mouse', category: 'Bordas' },
    { key: 'borderFocus', label: 'Bordas Focus', description: 'Bordas em foco', category: 'Bordas' },
    { key: 'divider', label: 'Divisores', description: 'Linhas divisórias', category: 'Bordas' },
    
    // Estados interativos
    { key: 'hover', label: 'Hover', description: 'Cor ao passar o mouse', category: 'Estados' },
    { key: 'active', label: 'Ativo', description: 'Cor do estado ativo', category: 'Estados' },
    { key: 'focus', label: 'Foco', description: 'Cor do estado em foco', category: 'Estados' },
    { key: 'disabled', label: 'Desabilitado', description: 'Cor de elementos desabilitados', category: 'Estados' },
    
    // Status e alertas
    { key: 'success', label: 'Sucesso', description: 'Cor para mensagens de sucesso', category: 'Status' },
    { key: 'warning', label: 'Aviso', description: 'Cor para avisos', category: 'Status' },
    { key: 'error', label: 'Erro', description: 'Cor para mensagens de erro', category: 'Status' },
    { key: 'info', label: 'Informação', description: 'Cor para informações', category: 'Status' },
    
    // Elementos específicos
    { key: 'input', label: 'Campos de Input', description: 'Fundo dos campos de entrada', category: 'Elementos' },
    { key: 'inputBorder', label: 'Bordas de Input', description: 'Bordas dos campos de entrada', category: 'Elementos' },
    { key: 'button', label: 'Botões', description: 'Fundo dos botões', category: 'Elementos' },
    { key: 'buttonText', label: 'Texto dos Botões', description: 'Texto dos botões', category: 'Elementos' },
    { key: 'link', label: 'Links', description: 'Cor dos links', category: 'Elementos' },
    { key: 'linkHover', label: 'Links Hover', description: 'Links ao passar o mouse', category: 'Elementos' }
  ]

  const presetColors = [
    '#3b82f6', // Blue
    '#ef4444', // Red
    '#10b981', // Green
    '#f59e0b', // Yellow
    '#8b5cf6', // Purple
    '#ec4899', // Pink
    '#06b6d4', // Cyan
    '#84cc16', // Lime
    '#f97316', // Orange
    '#6366f1'  // Indigo
  ]

  // Obter categorias únicas
  const categories = [...new Set(colorOptions.map(option => option.category))]
  
  // Filtrar opções por categoria ativa
  const filteredOptions = colorOptions.filter(option => option.category === activeCategory)

  const handleColorChange = (colorKey, colorValue) => {
    updateCustomColor(colorKey, colorValue)
    // Se não estiver no modo custom, mudar para custom
    if (theme !== 'custom') {
      setTheme('custom')
    }
  }

  const handlePresetClick = (color) => {
    handleColorChange(activeColor, color)
  }

  const handleReset = () => {
    resetCustomColors()
    if (theme === 'custom') {
      setTheme('light')
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-md mx-4">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-2">
            <Palette className="h-5 w-5 text-primary-600 dark:text-primary-400" />
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              Personalizar Cores
            </h3>
          </div>
          <button
            onClick={onClose}
            className="p-1 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-4 space-y-4">
          {/* Category Tabs */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
              Categoria:
            </label>
            <div className="flex flex-wrap gap-1">
              {categories.map((category) => (
                <button
                  key={category}
                  onClick={() => {
                    setActiveCategory(category)
                    // Definir a primeira cor da categoria como ativa
                    const firstOption = colorOptions.find(opt => opt.category === category)
                    if (firstOption) setActiveColor(firstOption.key)
                  }}
                  className={`px-3 py-1 text-xs rounded-md transition-colors ${
                    activeCategory === category
                      ? 'bg-primary-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600'
                  }`}
                >
                  {category}
                </button>
              ))}
            </div>
          </div>

          {/* Color Options */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
              Selecione o elemento para personalizar:
            </label>
            <div className="grid grid-cols-1 gap-2 max-h-48 overflow-y-auto">
              {filteredOptions.map((option) => (
                <button
                  key={option.key}
                  onClick={() => setActiveColor(option.key)}
                  className={`p-3 text-left rounded-lg border transition-colors ${
                    activeColor === option.key
                      ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                      : 'border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <div
                      className="w-4 h-4 rounded border border-gray-300 dark:border-gray-600"
                      style={{ backgroundColor: customColors[option.key] }}
                    />
                    <div>
                      <div className="font-medium text-gray-900 dark:text-white">
                        {option.label}
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">
                        {option.description}
                      </div>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Color Input */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
              Cor para {colorOptions.find(c => c.key === activeColor)?.label}:
            </label>
            <div className="flex gap-2">
              <input
                type="color"
                value={customColors[activeColor] || '#000000'}
                onChange={(e) => handleColorChange(activeColor, e.target.value)}
                className="w-12 h-10 rounded border border-gray-300 dark:border-gray-600 cursor-pointer"
              />
              <input
                type="text"
                value={customColors[activeColor] || '#000000'}
                onChange={(e) => handleColorChange(activeColor, e.target.value)}
                className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                placeholder="#000000"
              />
            </div>
          </div>

          {/* Preset Colors */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
              Cores predefinidas:
            </label>
            <div className="grid grid-cols-5 gap-2">
              {presetColors.map((color) => (
                <button
                  key={color}
                  onClick={() => handlePresetClick(color)}
                  className="w-10 h-10 rounded border-2 border-gray-300 dark:border-gray-600 hover:scale-110 transition-transform"
                  style={{ backgroundColor: color }}
                  title={color}
                />
              ))}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-4 border-t border-gray-200 dark:border-gray-700">
          <button
            onClick={handleReset}
            className="flex items-center gap-2 px-3 py-2 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 transition-colors"
          >
            <RotateCcw className="h-4 w-4" />
            Resetar
          </button>
          <button
            onClick={onClose}
            className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-colors"
          >
            Concluído
          </button>
        </div>
      </div>
    </div>
  )
}

export default ColorPicker